import { components } from '@doltech/dto';
import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { flatten, get } from 'lodash';
import { types as t } from 'mobx-state-tree';

import { UserTest } from '../../question-data-model/user-test.store';
import { ViewTestSubjectListeningTestSection } from './TestSection/view-test-subject-listening-test-section.store';
import { ViewTestSubjectReadingTestSection } from './TestSection/view-test-subject-reading-test-section.store';

const EMPTY_STRING = '';
const generateTableOfContent = (prefixText, testSections, showTestName = true) => {
  return testSections.map((testSection, testSectionIndex) => {
    const index = testSectionIndex + 1;
    const id = `test-section-${index}`;
    return {
      id,
      prefix: `${prefixText} ${index}${showTestName ? ':' : EMPTY_STRING}`,
      text: showTestName ? testSection.name : EMPTY_STRING,
    };
  });
};

export const ViewTestSubjectStore = UserTest.named('ViewTestSubjectStore')
  .props({
    initialed: t.optional(t.boolean, false),
    isBookmark: t.optional(t.boolean, false),
    testSections: t.optional(
      t.array(
        t.union(
          {
            dispatcher: (s) => {
              switch (s.skill) {
                case SkillEnum.READING:
                  return ViewTestSubjectReadingTestSection;
                case SkillEnum.LISTENING:
                  return ViewTestSubjectListeningTestSection;
                default:
                  throw new Error(`Cannot resolve Test Section skill "${s.skill}"`);
              }
            },
          },
          ViewTestSubjectReadingTestSection,
          ViewTestSubjectListeningTestSection
        )
      ),
      []
    ),
  })
  .actions((self) => ({
    setBookmark(mark: boolean) {
      self.isBookmark = mark;
    },
  }))
  .views((self) => ({
    getVocabList(): components['schemas']['VocabDTO'][] {
      const vocabList = self.testSections
        .map((testSection, testSectionIndex) => {
          const { vocab } = testSection;
          return vocab?.vocabs;
        })
        .filter(Boolean);
      return flatten(vocabList);
    },
    getVocabTableOfContent() {
      if (self.testType === TestTypeEnum.PRACTICE_TEST) {
        return [
          {
            id: `test-section-download-pdf`,
            text: 'Download pdf vocab',
            prefix: '📥 ',
          },
        ];
      }
      const prefixText = self.skill === SkillEnum.READING ? '📓 Passage' : '📓 Section';
      return [
        ...generateTableOfContent(prefixText, self.testSections),
        {
          id: `test-section-download-pdf`,
          text: 'Download pdf vocab',
          prefix: '📥 ',
        },
      ];
    },
    getTableOfContent() {
      const downloadPdfTitle =
        self.skill === SkillEnum.READING
          ? 'Download pdf đề & đáp án & giải thích'
          : 'Download pdf đề & đáp án & transcript';

      if (self.testType === TestTypeEnum.PRACTICE_TEST) {
        return [
          ...self.testSections[0].getTableOfContentForPracticeTest(),
          {
            id: `test-section-download-pdf`,
            text: downloadPdfTitle,
            prefix: '📥 ',
          },
        ];
      }
      const tableOfContents = self.testSections.map((testSection, testSectionIndex) => {
        return testSection.getTableOfContent(testSectionIndex);
      });
      return [
        ...flatten(tableOfContents),
        {
          id: `test-section-download-pdf`,
          text: downloadPdfTitle,
          prefix: '📥 ',
        },
      ];
    },
    get totalQuestions() {
      let count = 0;
      self.testSections.forEach((section) => {
        count += section.totalQuestionInGroup;
      });
      return count;
    },
    get answeredCount() {
      let count = 0;
      self.testSections.forEach((section) => {
        count += section.totalAnswerInGroup;
      });
      return count;
    },
    getMinMaxQuestionInGroup(sectionIndex, groupIndex) {
      const testSection = self.testSections.find((_n, sIndex) => sIndex === sectionIndex);
      if (testSection) {
        const { questions } = testSection.questionGroups.find(
          (_g, gIndex) => gIndex === groupIndex
        );
        const minQuestion = questions[0].no;
        const maxQuestion =
          Number(minQuestion) +
          questions.reduce((p, n) => p + get(n, 'bottomBarQuestionNoes.length', 0), 0) -
          1;
        if (maxQuestion > minQuestion) {
          return `${minQuestion} - ${maxQuestion}`;
        }
        return `${minQuestion}`;
      }
      return '';
    },
    getQuestions() {
      const questions = [];
      self.testSections.forEach((testSection: any) => {
        questions.push(...testSection.getQuestions());
      });
      return questions;
    },
  }));
